// Route '/play/:sessionid/:gameid/:playerid'
import React, { useContext, useState, useEffect, useRef } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import useInterval from 'use-interval';
import Countdown from 'react-countdown';
import Nav from '../../components/Nav/Nav';
import { StoreContext } from '../../utils/store';
import { ImCheckmark, ImCross } from 'react-icons/im';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { PrimaryButton } from '../../components/Buttons/Buttons';
import { VideoContainer } from '../EditQuestionPage/EditQuestionPage.styles';
import {
  MainContainer, QuestionContainer,
  QuestionStringContainer, ControlButton,
  AnswerInnerContainer, AnswerContainer,
  AnswerOuterContainer, AnswerAdminContainer,
  SelectionContainer, QuestionAdminContainer,
  MediaContainer, Image, MyCountdown
} from './PlayGamePage.styles';
import Radio from '@material-ui/core/Radio';

// material ui styles
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  formControl: {
    margin: theme.spacing(3),
  },
  checkboxLabel: {
    margin: 0,
  }
}));

const WhiteCheckbox = withStyles({
  root: {
    color: 'white',
    '&$checked': {
      color: 'white'
    },
  },
  checked: {},
})((props) => <Checkbox color="default" {...props} />);

const WhiteRadio = withStyles({
  root: {
    color: 'white',
    '&$checked': {
      color: 'white'
    },
  },
  checked: {},
})((props) => <Radio color="default" {...props} />);

// react icon styles
const iconStyles = {
  color: 'white',
  fontSize: '1em',
};

// answer card backgrounds
const backgroundColors = ['rgb(226, 27, 60)', 'rgb(19, 104, 206)', 'rgb(216, 158, 0)', 'rgb(38, 137, 12)', '#7d2ae8', 'rgb(235, 103, 16)'];

const PlayGamePage = () => {
  const { sessionid, playerid, gameid } = useParams();
  const context = useContext(StoreContext);
  const [api] = context.api;
  const [, setModalStatus] = context.modalStatus;
  const history = useHistory();

  // question states
  const [question, setQuestion] = useState({});
  const [results, setResults] = useState({});
  const [submitAnswer, setSubmitAnswer] = useState('0');
  const [submitAnswers, setSubmitAnswers] = useState([false, false, false, false, false, false]);
  const [correctAnswers, setCorrectAnswers] = useState([]);

  // admin control states
  const [advanced, setAdvanced] = useState(0);
  const [over, setOver] = useState(false);
  const [stop, setStop] = useState(false);

  const classes = useStyles();

  // countdown init
  const startTime = useRef(Date.now());
  // Renderer callback with condition
  const renderer = ({ seconds }) => {
    return <MyCountdown><p>{seconds}</p></MyCountdown>;
  };

  // player submit answer control functions
  const updateSubmitAnswers = async (e, idx) => {
    const newSubmitAnswers = [...submitAnswers];
    newSubmitAnswers[idx] = e.target.checked;
    console.log(newSubmitAnswers);
    setSubmitAnswers(newSubmitAnswers)
    const submit = [];
    newSubmitAnswers.forEach((ans, idx) => {
      if (ans) {
        submit.push(idx);
      }
    });
    await answerQuestion(submit);
  };

  const updateSubmitAnswer = async (e) => {
    setSubmitAnswer(parseInt(e.target.value, 10));
    const newSubmitAnswers = [...submitAnswers];
    for (let i = 0; i < question.answers.length; i++) {
      newSubmitAnswers[i] = false;
    }
    newSubmitAnswers[e.target.value] = true;
    setSubmitAnswers(newSubmitAnswers);
    const submit = [];
    newSubmitAnswers.forEach((ans, idx) => {
      if (ans) {
        submit.push(idx);
      }
    });
    await answerQuestion(submit);
  };

  // api call functions
  // player side
  const answerQuestion = async (answers) => {
    const response = await api.makeAPIRequest(`play/${playerid}/answer`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        answerIds: answers,
      })
    });
    try {
      const json = await response.json();
      if (response.status === 200) {
        console.log('answers:', answers);
      } else {
        console.log(json.error);
      }
    } catch (e) {
      console.log('ERROR', e);
      setModalStatus(status => ({
        ...status,
        modalActive: true,
        overlayActive: true,
        modalHeaderText: 'Error',
        modalBodyText: 'Sorry! Can\' connect to the server.',
        modalColors: 'error',
      }));
    }
  };

  const getStatus = async () => {
    const response = await api.makeAPIRequest(`play/${playerid}/status`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    try {
      const json = await response.json();
      if (response.status === 200) {
        return json.started;
      } else {
        console.log(json.error);
      }
    } catch (e) {
      console.log('ERROR', e);
      setModalStatus(status => ({
        ...status,
        modalActive: true,
        overlayActive: true,
        modalHeaderText: 'Error',
        modalBodyText: 'Sorry! Can\' connect to the server.',
        modalColors: 'error',
      }));
    }
  };

  const getQuestion = async () => {
    const response = await api.makeAPIRequest(`play/${playerid}/question`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    try {
      const json = await response.json();
      if (response.status === 200) {
        return json.question;
      } else {
        setModalStatus((status) => ({
          ...status,
          modalActive: true,
          overlayActive: true,
          modalHeaderText: 'Error',
          modalBodyText: json.error,
          modalColors: 'error',
        }));
      }
    } catch (e) {
      console.log('ERROR', e);
      setModalStatus(status => ({
        ...status,
        modalActive: true,
        overlayActive: true,
        modalHeaderText: 'Error',
        modalBodyText: 'Sorry! Can\' connect to the server.',
        modalColors: 'error',
      }));
    }
  };

  const getPlayerCorrectAnswers = async () => {
    const response = await api.makeAPIRequest(`play/${playerid}/answer`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    try {
      const json = await response.json();
      if (response.status === 200) {
        return json.answerIds;
      } else {
        setModalStatus(status => ({
          ...status,
          modalActive: true,
          overlayActive: true,
          modalHeaderText: 'Error',
          modalBodyText: json.error,
          modalColors: 'error',
        }));
      }
    } catch (e) {
      console.log('ERROR', e);
      setModalStatus(status => ({
        ...status,
        modalActive: true,
        overlayActive: true,
        modalHeaderText: 'Error',
        modalBodyText: 'Sorry! Can\' connect to the server.',
        modalColors: 'error',
      }));
    }
  };
  // admin side
  const getAdminQuestions = async () => {
    const response = await api.makeAPIRequest(`admin/session/${sessionid}/status`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    try {
      const json = await response.json();
      if (response.status === 200) {
        return json.results;
      } else {
        setModalStatus(status => ({
          ...status,
          modalActive: true,
          overlayActive: true,
          modalHeaderText: 'Error',
          modalBodyText: json.error,
          modalColors: 'error',
        }));
      }
    } catch (e) {
      console.log('ERROR', e);
      setModalStatus(status => ({
        ...status,
        modalActive: true,
        overlayActive: true,
        modalHeaderText: 'Error',
        modalBodyText: 'Sorry! Can\' connect to the server.',
        modalColors: 'error',
      }));
    }
  };

  const advanceGame = async () => {
    const response = await api.makeAPIRequest(`admin/quiz/${gameid}/advance`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    try {
      const json = await response.json();
      if (response.status === 200) {
        console.log('advanced');
        setAdvanced(a => a + 1);
      } else {
        setModalStatus(status => ({
          ...status,
          modalActive: true,
          overlayActive: true,
          modalHeaderText: 'Error',
          modalBodyText: json.error,
          modalColors: 'error',
        }));
      }
    } catch (e) {
      console.log('ERROR', e);
      setModalStatus(status => ({
        ...status,
        modalActive: true,
        overlayActive: true,
        modalHeaderText: 'Error',
        modalBodyText: 'Sorry! Can\' connect to the server.',
        modalColors: 'error',
      }));
    }
  };

  // admin control triggers
  useEffect(async () => {
    if (playerid === '0') {
      const r = await getAdminQuestions();
      setResults(r);
      if (r.position === r.questions.length) {
        setOver(true);
      }
    }
  }, [advanced]);

  // player poll for new quesiton or end of game
  useInterval(
    async () => {
      if (playerid !== '0') {
        const s = await getStatus();
        if (s) {
          const q = await getQuestion();
          // reset the countdown
          if (q.qString !== question.qString) {
            setSubmitAnswer('');
            setSubmitAnswers([false, false, false, false, false, false]);
            setStop(false);
            setModalStatus(status => ({
              ...status,
              modalActive: false,
              overlayActive: false,
            }));
            startTime.current = Date.now();
          }
          setQuestion(q);
        } else {
          console.log(over);
          setOver(true);
          setModalStatus(status => ({
            ...status,
            modalActive: false,
            overlayActive: false,
          }));
        }
      }
    },
    500
  );

  // show each correct when time is up for a question
  const isCorrect = (answers, corrects) => {
    const submit = [];
    answers.forEach((ans, idx) => {
      if (ans) {
        submit.push(idx);
      }
    })
    if (corrects.length !== submit.length) {
      return false;
    }
    for (let i = 0; i < submit.length; i++) {
      if (corrects[i] !== submit[i]) {
        return false;
      }
    }
    return true;
  };
  const showCorrectAnswers = async () => {
    setTimeout(async () => {
      setStop(true);
      const newCorrectAnswers = await getPlayerCorrectAnswers();
      setCorrectAnswers(newCorrectAnswers);
      console.log(newCorrectAnswers);
      console.log(submitAnswers);
      if (isCorrect(submitAnswers, newCorrectAnswers)) {
        setModalStatus(status => ({
          ...status,
          modalActive: true,
          overlayActive: true,
          modalHeaderText: 'Correct!',
          modalBodyText: 'Well done!',
          modalColors: 'success',
        }));
      } else {
        setModalStatus(status => ({
          ...status,
          modalActive: true,
          overlayActive: true,
          modalHeaderText: 'Incorret',
          modalBodyText: <>
                          <p>Tough Quesiton!</p>
                          <p>{`Correct Answer(s): ${newCorrectAnswers.map(ans => question.answers[ans])}`}</p>
                        </>,
          modalColors: 'error',
        }));
      }
    }, 600);
  };

  return (
    <section>
      <Nav
        visibilities={['false', 'false', 'false', 'true', 'false']}
        exit={() => history.push('/')}
      />
      <MainContainer>
        {playerid === '0'
          // admin side
          ? <>
            {results.questions && results.position >= 0 && results.position < results.questions.length
              ? <div className={classes.root}>
                <QuestionContainer>
                  <QuestionAdminContainer>

                    <h1>{results.questions[results.position].qString}</h1>
                    <ControlButton>
                      <PrimaryButton
                        onClick={advanceGame}
                      >
                        {results.position === results.questions.length - 1 ? 'End the game' : 'Next Question'}
                      </PrimaryButton>
                    </ControlButton>
                  </QuestionAdminContainer>
                  {results.questions[results.position].mediaType &&
                    <>
                      {results.questions[results.position].mediaType === 'img'
                        ? <>
                          {results.questions[results.position].img
                            ? <MediaContainer>
                              <Image src={results.questions[results.position].img} alt={results.questions[results.position].qString} />
                            </MediaContainer>
                            : <></>}
                        </>
                        : <MediaContainer>
                          <VideoContainer
                            src={results.questions[results.position].video}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen>
                          </VideoContainer>
                        </MediaContainer>
                      }
                    </>
                  }
                  <AnswerAdminContainer>
                    <AnswerOuterContainer>
                      {results.questions[results.position].answers.map((ans, idx) => (
                        <AnswerContainer
                          key={idx}
                          style={{ backgroundColor: backgroundColors[idx] }}
                        >
                          <AnswerInnerContainer>
                            <span>{ans}</span>
                          </AnswerInnerContainer>
                          {results.questions[results.position].correctAnswers[idx]
                            ? <ImCheckmark style={iconStyles} />
                            : <ImCross style={iconStyles} />
                          }
                        </AnswerContainer>
                      ))}
                    </AnswerOuterContainer>
                  </AnswerAdminContainer>
                </QuestionContainer>
              </div>
              : <p>Loading...</p>
            }
            {over && <Redirect to={`/game/result/${sessionid}`} />}
          </>
          // player side
          : <>
            {question.qString !== undefined
              ? <div className={classes.root}>
                <Countdown date={startTime.current + question.timeLimit * 1000}
                  renderer={renderer}
                  onComplete={showCorrectAnswers}
                />
                <QuestionContainer>
                  <QuestionStringContainer>
                    <h1>{question.qString}</h1>
                  </QuestionStringContainer>
                  {question.mediaType &&
                    <>
                      {question.mediaType === 'img'
                        ? <>
                          {question.img &&
                            <MediaContainer>
                              <Image src={question.img} alt={question.qString} />
                            </MediaContainer>
                          }
                          </>
                        : <MediaContainer>
                            <VideoContainer
                              src={question.video}
                              title="YouTube video player"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen>
                            </VideoContainer>
                          </MediaContainer>
                      }</>
                  }
                  <FormControl component="fieldset" className={classes.formControl}>
                    {question.type === 'Multiple'
                      ? <FormGroup>
                        <AnswerOuterContainer>
                          {question.answers.map((ans, idx) => (
                            <AnswerContainer
                              key={idx}
                              style={{
                                backgroundColor: backgroundColors[idx],
                                opacity: stop && (correctAnswers ? !correctAnswers.includes(idx) : true) ? 0.3 : 1,
                              }}
                            >
                              <AnswerInnerContainer>
                                <span>{ans}</span>
                              </AnswerInnerContainer>
                              {stop
                                ? <>
                                  {(correctAnswers ? correctAnswers.includes(idx) : true)
                                    ? <ImCheckmark style={iconStyles} />
                                    : <ImCross style={iconStyles} />
                                  }</>
                                : <SelectionContainer>
                                  <FormControlLabel
                                    className={classes.checkboxLabel}
                                    control={
                                      <WhiteCheckbox
                                        disabled={stop}
                                        checked={submitAnswers[idx]}
                                        onChange={e => updateSubmitAnswers(e, idx)}
                                        inputProps={{ 'aria-label': 'answer checkbox' }}
                                      />}
                                  />
                                </SelectionContainer>
                              }
                            </AnswerContainer>
                          ))}
                        </AnswerOuterContainer>
                      </FormGroup>
                      : <AnswerOuterContainer>
                        {question.answers.map((ans, idx) => (
                          <AnswerContainer
                            key={idx}
                            style={{
                              backgroundColor: backgroundColors[idx],
                              opacity: stop && (correctAnswers ? !correctAnswers.includes(idx) : true) ? 0.3 : 1,
                            }}
                          >
                            <AnswerInnerContainer>
                              <span>{ans}</span>
                            </AnswerInnerContainer>
                            {stop
                              ? <>
                                {(correctAnswers ? correctAnswers.includes(idx) : true)
                                  ? <ImCheckmark style={iconStyles} />
                                  : <ImCross style={iconStyles} />
                                }</>
                              : <SelectionContainer>
                                <WhiteRadio
                                  disabled={stop}
                                  checked={submitAnswer === idx}
                                  onChange={updateSubmitAnswer}
                                  value={idx}
                                  name="choose-answer-radio"
                                  inputProps={{ 'aria-label': 'answer radio' }}
                                />
                              </SelectionContainer>}
                          </AnswerContainer>
                        ))}
                      </AnswerOuterContainer>
                    }
                  </FormControl>
                </QuestionContainer>
              </div>
              : <p>Loading...</p>
            }
            {over && <Redirect to={`/play/result/${playerid}`} />}
          </>
        }
      </MainContainer>
    </section>
  );
};

export default PlayGamePage;
